"use client";

import { useQuery } from "@tanstack/react-query";
import { useContext, useState, useEffect } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { usePersonNameQuery } from "@/hooks/persons";
import { personsMapOptions } from "@/queryOptions/persons";
import { voiceIdPersonMapOptions } from "@/queryOptions/voices";
import { FilterParamsContext } from "@/stories/SearchDrawerComponents/FilterParamsProvider";

// This is so annoyingly complicated I hate you guys so much
const useSelectedPersonIds = () => {
  const { filterParams } = useContext(FilterParamsContext);
  const [selectedPersonIds, setselectedPersonIds] = useState<number[]>([]);
  // Get the currently selected person IDs from voice IDs
  const { data: voiceMap } = useQuery(voiceIdPersonMapOptions());
  const personIds: Set<number> = new Set();
  filterParams.voice_ids?.forEach((voiceId) => {
    const person = voiceMap?.get(voiceId);
    if (person) {
      personIds.add(person.id);
    }
  });

  const personIdsString = JSON.stringify(Array.from(personIds));
  useEffect(() => {
    // I need selectedPersonIds to persist through renders
    setselectedPersonIds(JSON.parse(personIdsString));
  }, [personIdsString]);
  return selectedPersonIds;
};

export default function SpeakerSelect() {
  const { filterParams, setFilterParams } = useContext(FilterParamsContext);
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const selectedPersonIds = useSelectedPersonIds();

  const { data: personMap } = useQuery(personsMapOptions());

  // Debounce the input value
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [inputValue]);

  // Use react-query to fetch organizations with debounced input
  const { data: options = [], isLoading: loading } =
    usePersonNameQuery(debouncedInputValue);
  const optionMap = new Map(options.map((person) => [person.id, person]));

  return (
    <Autocomplete
      multiple
      options={options.map((person) => person.id)}
      value={selectedPersonIds}
      onChange={(_event, newValue) => {
        const voiceIds: number[] = [];
        newValue.forEach((personId) => {
          const person = optionMap.get(personId);
          if (person) {
            person.voices.forEach((voice) => {
              voiceIds.push(voice.id);
            });
          }
        });
        setFilterParams({
          ...filterParams,
          voice_ids: voiceIds,
        });
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => <TextField {...params} label="Speaker" />}
      getOptionLabel={(option) => personMap?.get(option)?.name || "..."}
      getOptionKey={(option) => option}
      loading={loading}
      disableCloseOnSelect
      filterOptions={(x) => x} // Disable built-in filtering since we're using API search
    />
  );
}
